// 주간 시간표 공용 주 점프 — 기간 제목을 누르면 미니 달력이 열리고, 주(행)를 누르면 그 주로 이동한다.
// 관리자/선생님 레슨 시간표(v1, v2)의 sticky 주간 이동 바에서 쓴다.
// 이동 바 높이가 고정이라 펼침 대신 Popover로 띄운다.
import React, { useState } from "react";
import { Box, Button, ButtonBase, IconButton, Popover, Typography } from "@mui/material";
import { ArrowDropDown, ArrowDropUp, ChevronLeft, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";
import { useDarkMode } from "../App";

const WEEKDAY_LABELS = ["월", "화", "수", "목", "금", "토", "일"];

// 시간표의 "이번 주" 시작(월요일) — 시간표 초기값과 같은 규칙
export function getCurrentWeekStart() {
  return dayjs().startOf("week").add(1, "day").format("YYYY-MM-DD");
}

// 해당 달을 덮는 주(월요일 시작) 목록 — 각 원소는 그 주 월요일
function weeksOfMonth(year, month) {
  const first = dayjs(new Date(year, month, 1));
  let ws = first.subtract((first.day() + 6) % 7, "day");
  const weeks = [];
  while (ws.isBefore(first.endOf("month")) || ws.isSame(first.endOf("month"), "day")) {
    weeks.push(ws);
    ws = ws.add(7, "day");
  }
  return weeks;
}

export default function WeekJumpPicker({ weekStart, label, onChange, labelSx }) {
  const { darkMode } = useDarkMode();
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewYm, setViewYm] = useState(() => dayjs(weekStart).startOf("month"));
  const today = dayjs();

  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setViewYm(dayjs(weekStart).startOf("month"));
    setAnchorEl(e.currentTarget);
  };

  const pick = (ws) => {
    setAnchorEl(null);
    if (ws !== weekStart) onChange(ws);
  };

  const selectedBg = darkMode ? "#1565c0" : "#1976d2";

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        aria-label="주 선택"
        sx={{ borderRadius: 2, pl: 0.75, pr: 0.25, py: 0.25, minWidth: 0, "&:hover": { bgcolor: "action.hover" } }}
      >
        <Typography sx={{ whiteSpace: "nowrap", ...labelSx }}>{label}</Typography>
        {open ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { p: 1.5, width: 300, maxWidth: "calc(100vw - 32px)" } } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <IconButton size="small" aria-label="이전 달" onClick={() => setViewYm(viewYm.subtract(1, "month"))}>
            <ChevronLeft />
          </IconButton>
          <Typography fontWeight="bold">{viewYm.format("YYYY년 M월")}</Typography>
          <IconButton size="small" aria-label="다음 달" onClick={() => setViewYm(viewYm.add(1, "month"))}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
          {WEEKDAY_LABELS.map((d, i) => (
            <Typography
              key={d}
              variant="caption"
              sx={{ textAlign: "center", fontWeight: "bold", color: i === 6 ? "#d32f2f" : "text.secondary" }}
            >
              {d}
            </Typography>
          ))}
        </Box>

        {weeksOfMonth(viewYm.year(), viewYm.month()).map((ws) => {
          const wsStr = ws.format("YYYY-MM-DD");
          const selected = wsStr === weekStart;
          return (
            <ButtonBase
              key={wsStr}
              onClick={() => pick(wsStr)}
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                borderRadius: 2,
                mb: 0.25,
                bgcolor: selected ? selectedBg : "transparent",
                color: selected ? "#fff" : "inherit",
                "&:hover": selected ? {} : { bgcolor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd" },
              }}
            >
              {Array.from({ length: 7 }, (_, i) => {
                const d = ws.add(i, "day");
                const inMonth = d.month() === viewYm.month();
                const isToday = d.isSame(today, "day");
                return (
                  <Box
                    key={i}
                    sx={{
                      py: 0.75,
                      fontSize: "0.85rem",
                      textAlign: "center",
                      borderRadius: 1.5,
                      fontWeight: isToday ? "bold" : "normal",
                      border: isToday ? "1.5px solid #ff9800" : "1.5px solid transparent",
                      color: selected ? "inherit" : i === 6 ? "#d32f2f" : "inherit",
                      opacity: inMonth ? 1 : 0.4,
                    }}
                  >
                    {d.date()}
                  </Box>
                );
              })}
            </ButtonBase>
          );
        })}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button size="small" onClick={() => pick(getCurrentWeekStart())}>
            이번 주
          </Button>
        </Box>
      </Popover>
    </>
  );
}
